import { ReactNode } from "react";
import { Pressable, View } from "react-native";
import { Link, type Href } from "expo-router";
import { Text } from "@src/components";
import { Icon, IconProps } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";

interface ReportsListItemBaseProps {
  /** Item label text */
  title: string;
  /**
   * Single-paragraph subtitle shown below the title.
   * For multi-paragraph copy, pass `<ReportsListItemText>` children instead.
   * If `children` is provided, `subtitle` is ignored.
   */
  subtitle?: string;
  /**
   * Optional rich subtitle content. Use one `<ReportsListItemText>` per paragraph
   * to control line breaks without relying on `\n` inside a string.
   */
  children?: ReactNode;
  /** Whether to show the chevron icon (default: true) */
  showChevron?: boolean;
  /** Icon to use for the chevron (default: "chevronRight") */
  chevronIcon?: IconProps["name"];
}

export type ReportsListItemProps = ReportsListItemBaseProps &
  // Either an `href` (rendered as a real `<a>` so cmd/middle-click → new tab works)
  // or an `onPress` handler for non-navigational actions (e.g. opening external URLs in `_blank`).
  ({ href: Href; onPress?: never } | { href?: undefined; onPress: () => void });

export function ReportsListItem(props: ReportsListItemProps) {
  const { theme } = useUnistyles();
  const { title, subtitle, children, showChevron = true, chevronIcon = "chevronRight" } = props;

  // Card layout lives on an inner View so it survives expo-router's `<Link asChild>`
  // style overriding. The outer Pressable is intentionally unstyled.
  const card = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: theme.spacing["3xl"],
        paddingHorizontal: theme.spacing.xl,
        backgroundColor: theme.colors.surfaceCard,
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        borderRadius: theme.spacing.md,
        minHeight: 56,
      }}
    >
      <View style={{ flex: 1, gap: theme.spacing.md }}>
        <Text textStyle="sm/regular" color="text">
          {title}
        </Text>
        {children ?
          <View style={{ gap: theme.spacing.md }}>{children}</View>
        : subtitle && (
            <Text textStyle="xs/regular" color="textMuted">
              {subtitle}
            </Text>
          )
        }
      </View>
      {showChevron && <Icon name={chevronIcon} size={theme.spacing["6xl"]} color={theme.colors.textMuted} />}
    </View>
  );

  // `<Link href asChild>` clones the Pressable so the rendered DOM is a real `<a href>`,
  // enabling cmd/middle-click → new tab and right-click → "Open in new tab".
  if (props.href) {
    return (
      <Link href={props.href} asChild>
        <Pressable accessibilityRole="link" style={{ cursor: "pointer" }}>
          {card}
        </Pressable>
      </Link>
    );
  }

  return (
    <Pressable accessibilityRole="button" onPress={props.onPress} style={{ cursor: "pointer" }}>
      {card}
    </Pressable>
  );
}

export interface ReportsListItemTextProps {
  children: ReactNode;
}

/**
 * One paragraph of subtitle copy inside a `<ReportsListItem>`.
 * Render multiple of these as siblings to produce blank-line-separated paragraphs
 * without using `\n` in strings.
 */
export function ReportsListItemText({ children }: ReportsListItemTextProps) {
  return (
    <Text textStyle="xs/regular" color="textMuted">
      {children}
    </Text>
  );
}
